import React from "react";
import { View, Text, useWindowDimensions } from "react-native";
import {
  InputPatientDetail,
  InputPatientDetailDropdown,
  NormalText,
  PrimaryButton,
  RainbowBorder,
} from "../../../common/native";
import { InputPatientDetailSurgeonPhoto } from "../../../common/web";
import { TAppearance } from "./AppearanceTab";
import { DEPLOY_WIDGET_TOOLTIPS } from "./deployWidgetTooltips";
import { TDeployWidgetOnboardingStepId } from "./deployWidgetOnboardingContent";
import { SessionContext } from "../../../../contexts";
import {
  TDeployFormName,
  TFormFormatConfig,
  buildFormPreviewUrl,
} from "./FormFormatTab";
import {
  postClinicPhoto,
  postClinicProfile,
} from "../../../../services/api-clinic";

export type TProfile = {
  email: string;
  logoUrl: Blob | string | null;
  logoUrlRectangle: Blob | string | null;
};

type ProfileTabProps = {
  clinicSdkKey?: string;
  profile: TProfile;
  setProfile: React.Dispatch<React.SetStateAction<TProfile>>;
  appearance: TAppearance;
  formFormat: TFormFormatConfig;
  showAllFormTypes?: boolean;
  shouldRenderPreview: boolean;
  highlightedStepId?: TDeployWidgetOnboardingStepId;
  onUpdateFormFormat: (field: keyof TFormFormatConfig, value: string) => void;
  onContinue: () => void;
};

const FORM_OPTIONS: { value: TDeployFormName; label: string }[] = [
  { value: "onboarding-tier1", label: "Full Form (Recommended)" },
  { value: "onboarding-scan", label: "Onboarding Scan" },
  { value: "onboarding-scan-all-poses", label: "Onboarding Scan (All Poses)" },
  { value: "onboarding-advertise", label: "Onboarding Advertise" },
  { value: "onboarding-chatbot", label: "Onboarding Chatbot" },
  { value: "onboarding-tier1-chatbot", label: "Onboarding Tier1 Chatbot" },
  {
    value: "onboarding-single-procedure",
    label: "Onboarding Single Procedure",
  },
  { value: "onboarding-lite", label: "Embedded iframe Form" },
];

const LANGUAGE_OPTIONS: { value: TFormFormatConfig["lang"]; label: string }[] =
  [
    { value: "en", label: "English" },
    { value: "es", label: "Spanish" },
    { value: "tr", label: "Turkish" },
    { value: "pt", label: "Portuguese" },
    { value: "fr", label: "French" },
    { value: "th", label: "Thai" },
    { value: "ar", label: "Arabic" },
    { value: "ko", label: "Korean" },
    { value: "de", label: "German" },
  ];

const FORM_PREVIEW_MAX_WIDTH = 375;
const DEPLOY_WIDGET_TOOLTIP_EVENT = "Deploy Widget Tooltip Opened";

const ProfileTab: React.FC<ProfileTabProps> = ({
  clinicSdkKey,
  profile,
  setProfile,
  appearance,
  formFormat,
  showAllFormTypes = false,
  shouldRenderPreview,
  highlightedStepId,
  onUpdateFormFormat,
  onContinue,
}) => {
  const { width, height } = useWindowDimensions();
  const isMobile = Math.min(width, height) < 644;
  const sessionContext = React.useContext(SessionContext);
  const session = sessionContext?.session;
  const setSession = sessionContext?.setSession;

  const [error, setError] = React.useState<string>("");
  const [logoUploadError, setLogoUploadError] = React.useState<string>("");
  const [isLogoUploading, setIsLogoUploading] = React.useState<boolean>(false);
  const [isProfileSaving, setIsProfileSaving] = React.useState<boolean>(false);
  const [logoPreviewVersion, setLogoPreviewVersion] = React.useState<number>(0);
  const logoUploadAttemptRef = React.useRef(0);
  const lastSavedEmailRef = React.useRef("");
  const canPreview = Boolean(clinicSdkKey);
  const previewUrl = React.useMemo(() => {
    if (!canPreview) return "";

    const url = new URL(
      buildFormPreviewUrl(clinicSdkKey as string, appearance, formFormat),
    );

    if (logoPreviewVersion > 0) {
      url.searchParams.set("logoPreviewVersion", String(logoPreviewVersion));
    }

    return url.toString();
  }, [canPreview, clinicSdkKey, appearance, formFormat, logoPreviewVersion]);

  const isProfileComplete = profile.email.trim().length > 0;

  const visibleFormOptions = React.useMemo(() => {
    if (showAllFormTypes) return FORM_OPTIONS;
    return FORM_OPTIONS.filter((option) =>
      ["onboarding-tier1", "onboarding-lite"].includes(option.value),
    );
  }, [showAllFormTypes]);

  const labelByValue = React.useMemo(
    () =>
      Object.fromEntries(
        FORM_OPTIONS.map((option) => [option.value, option.label]),
      ) as Record<TDeployFormName, string>,
    [],
  );

  const valueByLabel = React.useMemo(
    () =>
      Object.fromEntries(
        FORM_OPTIONS.map((option) => [option.label, option.value]),
      ) as Record<string, TDeployFormName>,
    [],
  );
  const languageLabelByValue = React.useMemo(
    () =>
      Object.fromEntries(
        LANGUAGE_OPTIONS.map((option) => [option.value, option.label]),
      ) as Record<TFormFormatConfig["lang"], string>,
    [],
  );
  const languageValueByLabel = React.useMemo(
    () =>
      Object.fromEntries(
        LANGUAGE_OPTIONS.map((option) => [option.label, option.value]),
      ) as Record<string, TFormFormatConfig["lang"]>,
    [],
  );

  const hasAutoSelectedLanguageRef = React.useRef(false);
  React.useEffect(() => {
    if (hasAutoSelectedLanguageRef.current) return;
    hasAutoSelectedLanguageRef.current = true;
    if (typeof navigator === "undefined") return;

    const localeCandidates = [
      ...(navigator.languages ?? []),
      navigator.language,
    ].filter(Boolean) as string[];
    if (!localeCandidates.length) return;

    const supportedLanguages = new Set(
      LANGUAGE_OPTIONS.map((option) => option.value),
    );

    let detectedLanguage: TFormFormatConfig["lang"] | null = null;
    for (const locale of localeCandidates) {
      const normalized = locale.toLowerCase();
      const languageCode = normalized.split(
        "-",
      )[0] as TFormFormatConfig["lang"];
      if (supportedLanguages.has(languageCode)) {
        detectedLanguage = languageCode;
        break;
      }
    }

    if (!detectedLanguage || detectedLanguage === formFormat.lang) return;
    onUpdateFormFormat("lang", detectedLanguage);
  }, [formFormat.lang, onUpdateFormFormat]);

  const handleOnClickContinue = async () => {
    const email = profile.email.trim();

    if (!email) {
      setError("Email address is required");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Invalid email address format");
      return;
    }

    if (email === lastSavedEmailRef.current) {
      onContinue();
      return;
    }

    if (!session) {
      setError("Unable to save the email. Refresh the page and try again.");
      return;
    }

    setIsProfileSaving(true);
    setError("");
    try {
      await postClinicProfile(session, { emails: [email] });
      lastSavedEmailRef.current = email;
      onContinue();
    } catch {
      setError("Email could not be saved. Please try again.");
    } finally {
      setIsProfileSaving(false);
    }
  };

  const handleLogoSelect = React.useCallback(
    async (imageBlob: Blob) => {
      setProfile((previous) => ({ ...previous, logoUrl: imageBlob }));
      setLogoUploadError("");

      if (!session) {
        setLogoUploadError(
          "Unable to upload logo right now. Refresh and try again.",
        );
        return;
      }

      const attemptId = logoUploadAttemptRef.current + 1;
      logoUploadAttemptRef.current = attemptId;
      setIsLogoUploading(true);

      try {
        const postClinicPhotoRes = await postClinicPhoto(session, {
          imageBlob,
        });
        await postClinicProfile(session, {
          logo_url: postClinicPhotoRes.photo_url,
        });

        if (logoUploadAttemptRef.current !== attemptId) return;

        setProfile((previous) => ({
          ...previous,
          logoUrl: postClinicPhotoRes.photo_url,
        }));
        setSession?.((previous) =>
          previous
            ? {
                ...previous,
                logoUrl: postClinicPhotoRes.photo_url,
              }
            : previous,
        );
        setLogoPreviewVersion(Date.now());
      } catch {
        if (logoUploadAttemptRef.current !== attemptId) return;
        setLogoUploadError("Logo upload failed. Please try again.");
      } finally {
        if (logoUploadAttemptRef.current === attemptId) {
          setIsLogoUploading(false);
        }
      }
    },
    [session, setProfile, setSession],
  );

  return (
    <View style={{ flex: 1, gap: 10, marginTop: 10 }}>
      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
        <View
          style={{
            flex: 1,
            width: "100%",
            minWidth: 350,
            maxWidth: 550,
            gap: 10,
          }}
        >
          <NormalText text="Set up your profile" fontWeight="600" />
          <View style={{ maxWidth: 550, gap: 10 }}>
            <RainbowBorder
              isActive={highlightedStepId === "profile-essentials"}
            >
              <InputPatientDetail
                name={"Email for notifications"}
                textWidth={200}
                placeholder="email address (required)"
                value={profile.email}
                tooltipText={
                  DEPLOY_WIDGET_TOOLTIPS.profile.leadNotificationEmail
                }
                tooltipTrackingEventName={DEPLOY_WIDGET_TOOLTIP_EVENT}
                tooltipTrackingProperties={{ Tab: "Profile" }}
                onChangeText={(text) => {
                  setError("");
                  setProfile({ ...profile, email: text });
                }}
              />
            </RainbowBorder>
            <InputPatientDetailSurgeonPhoto
              name={"Clinic logo"}
              textWidth={200}
              uploadSizePx={56.25}
              value={profile.logoUrl}
              onPhotoSelect={handleLogoSelect}
              tooltipText={DEPLOY_WIDGET_TOOLTIPS.profile.clinicLogo}
              tooltipTrackingEventName={DEPLOY_WIDGET_TOOLTIP_EVENT}
              tooltipTrackingProperties={{ Tab: "Profile" }}
            />
            {isLogoUploading ? (
              <Text
                style={{
                  fontFamily: "Urbanist500",
                  fontWeight: "500",
                  color: "#475467",
                }}
              >
                Uploading logo and refreshing preview...
              </Text>
            ) : null}
            {logoUploadError ? (
              <Text
                style={{
                  fontFamily: "Urbanist500",
                  fontWeight: "500",
                  color: "red",
                }}
              >
                {logoUploadError}
              </Text>
            ) : null}
            {/* <InputPatientDetailSurgeonPhoto
              name={"Clinic logo rectangle"}
              uploadSizePx={56.25}
              value={profile.logoUrlRectangle}
              onPhotoSelect={(imageBlob) =>
                setProfile({ ...profile, logoUrlRectangle: imageBlob })
              }
            /> */}
            {error ? (
              <Text
                style={{
                  fontFamily: "Urbanist500",
                  fontWeight: "500",
                  color: "red",
                }}
              >
                {error}
              </Text>
            ) : null}
          </View>
          <View style={{ width: "100%", maxWidth: 440, zIndex: 9 }}>
            <InputPatientDetailDropdown
              name="Form Type"
              textWidth={200}
              value={[labelByValue[formFormat.formName]]}
              selections={visibleFormOptions.map((option) => option.label)}
              tooltipText={DEPLOY_WIDGET_TOOLTIPS.profile.formType}
              tooltipTrackingEventName={DEPLOY_WIDGET_TOOLTIP_EVENT}
              tooltipTrackingProperties={{ Tab: "Profile" }}
              onSelect={(value) =>
                onUpdateFormFormat("formName", valueByLabel[value[0]])
              }
            />
          </View>
          <View style={{ width: "100%", maxWidth: 440, zIndex: 8 }}>
            <InputPatientDetailDropdown
              name="Default Language"
              textWidth={200}
              value={[languageLabelByValue[formFormat.lang]]}
              selections={LANGUAGE_OPTIONS.map((option) => option.label)}
              tooltipText={DEPLOY_WIDGET_TOOLTIPS.profile.defaultLanguage}
              tooltipTrackingEventName={DEPLOY_WIDGET_TOOLTIP_EVENT}
              tooltipTrackingProperties={{ Tab: "Profile" }}
              onSelect={(value) =>
                onUpdateFormFormat("lang", languageValueByLabel[value[0]])
              }
            />
          </View>
        </View>
        <View
          style={{
            flex: 1,
            width: "100%",
            minWidth: 350,
            gap: 10,
          }}
        >
          <NormalText text={"Form Preview"} fontWeight="600" />
          <RainbowBorder
            isActive={highlightedStepId === "profile-preview"}
            style={{
              width: "100%",
              maxWidth: FORM_PREVIEW_MAX_WIDTH,
              alignSelf: "flex-start",
            }}
          >
            <View
              style={{
                position: "relative",
                aspectRatio: 3 / 5,
                width: "100%",
                maxWidth: FORM_PREVIEW_MAX_WIDTH,
                borderRadius: 10,
                overflow: "hidden",
                marginTop: 10,
                borderWidth: 1,
                borderColor: "#DDDEE0",
                backgroundColor: "#F8F8F8",
              }}
            >
              {shouldRenderPreview && canPreview && previewUrl ? (
                <iframe
                  src={previewUrl}
                  allow="autoplay"
                  allowFullScreen
                  sandbox="allow-forms allow-same-origin allow-scripts"
                  style={{
                    position: "absolute",
                    border: 0,
                    width: "100%",
                    height: "100%",
                    pointerEvents: "auto",
                  }}
                  scrolling="yes"
                >
                  Browser does not support iframe
                </iframe>
              ) : (
                <View
                  style={{
                    flex: 1,
                    justifyContent: "center",
                    alignItems: "center",
                    padding: 20,
                  }}
                >
                  <Text style={{ fontFamily: "Urbanist500", color: "#868B97" }}>
                    {canPreview
                      ? "Open this tab to load preview."
                      : "Add a clinic SDK key to load preview."}
                  </Text>
                </View>
              )}
            </View>
          </RainbowBorder>
          {previewUrl ? (
            <Text
              numberOfLines={2}
              style={{
                fontFamily: "Urbanist500",
                fontSize: 12,
                color: "#868B97",
              }}
            >
              {previewUrl}
            </Text>
          ) : null}
        </View>
      </View>
      <View
        style={{
          marginTop: "auto",
          justifyContent: "center",
          alignContent: "center",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <PrimaryButton
          text={"Next: Appearance"}
          disabled={!isProfileComplete || isLogoUploading}
          loading={isProfileSaving}
          roundBorderRadius
          width={275}
          onPress={() => handleOnClickContinue()}
        />
      </View>
    </View>
  );
};

export default ProfileTab;
